import { getRequestExpensesCategories } from 'lib/api/requestExpenses';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CreateRequestExpensesCategory from './components/CreateRequestExpensesCategory';
import RequestExpensesCategorySummary from './components/RequestExpensesCategorySummary';

export type RequestExpensesCategory = {
  id: number;
  name: string;
  hasIva: boolean;
};

const RequestExpensesCategories: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<RequestExpensesCategory[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<RequestExpensesCategory | null>(null);
  const [openCreateCategory, setOpenCreateCategory] = useState(false);
  const [openCategorySummary, setOpenCategorySummary] = useState(false);

  const pageActions: CustomButtonProps[] = [
    {
      action: () => setOpenCreateCategory(true),
      name: 'Crear categoría',
      color: 'primary',
    },
  ];

  const handleOpenCategory = (id: number) => {
    const category = categories.find((x) => x.id === id);
    if (!category) return showAlert(dispatch, 'error', 'Ha ocurrido un error');
    setSelectedCategory(category);
    setOpenCategorySummary(true);
  };

  const setUp = async () => {
    setLoading(true);
    try {
      const categories = await getRequestExpensesCategories(dispatch);
      setCategories(categories);
    } catch (error) {
      console.error('Error fetching request expenses categories:', error);
      showAlert(dispatch, 'error', 'Error al obtener las categorías de gastos');
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  if (loading) {
    return <CenteredCircularProgress />;
  }

  return (
    <div>
      <HeaderWithActions
        title="Aquí encontrarás todas las categorías de gastos."
        variant="body1"
        buttons={pageActions}
        type="subHeader"
      />
      <EnhancedTable
        dataHeaders={[
          { key: 'id', label: 'ID' },
          {
            key: 'name',
            label: 'Nombre',
          },
          {
            key: 'hasIva',
            label: 'Tiene IVA',
            type: 'boolean-chip',
          },
        ]}
        data={categories}
        rowsPerPageDefault={10}
        handleClickRow={handleOpenCategory}
      />
      {openCategorySummary && (
        <RequestExpensesCategorySummary
          category={selectedCategory}
          open={openCategorySummary}
          setOpen={setOpenCategorySummary}
          setUp={setUp}
        />
      )}
      {openCreateCategory && (
        <CreateRequestExpensesCategory
          open={openCreateCategory}
          setOpen={setOpenCreateCategory}
          setUp={setUp}
        />
      )}
    </div>
  );
};

export default RequestExpensesCategories;
